import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import VideoPlayer from '../components/VideoPlayer';

const mutationHeaders = { headers: { 'x-training-request': '1' }, withCredentials: true };

function normalizeModule(module, index) {
  return {
    ...module,
    id: module.id || module.moduleId || module.key,
    title: module.title || `Training Module ${index + 1}`,
    description: module.description || '',
    streamUrl: module.streamUrl || module.url,
    completed: Boolean(module.completed || module.completedAt),
    available: module.available ?? module.unlocked ?? false,
  };
}

export default function VideoTraining({ userData, onComplete }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reviewKey = searchParams.get('review');
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  const loadModules = useCallback(async () => {
    const { data } = await axios.get('/api/training/modules', { withCredentials: true });
    const nextModules = (data.modules || []).map(normalizeModule);
    setModules(nextModules);
    const firstIncomplete = nextModules.findIndex(module => !module.completed);
    setCurrentIndex(current => {
      if (reviewKey) { const idx=reviewKey==='role'?nextModules.length-1:nextModules.findIndex(m=>m.moduleKey===reviewKey);if(idx>=0)return idx; }
      if (nextModules[current]?.completed && firstIncomplete >= 0) return firstIncomplete;
      return Math.min(current, Math.max(nextModules.length - 1, 0));
    });
    return nextModules;
  }, [reviewKey]);

  useEffect(() => {
    let active = true;
    loadModules()
      .catch(() => active && setError('Unable to load your assigned training. Please refresh and try again.'))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [loadModules]);

  const currentModule = modules[currentIndex];
  const completedCount = modules.filter(module => module.completed).length;
  const allCompleted = modules.length > 0 && completedCount === modules.length;
  const overallPercent = modules.length ? (completedCount / modules.length) * 100 : 0;

  const modulePath = (suffix = '') =>
    `/api/training/modules/${encodeURIComponent(currentModule.id)}${suffix}`;

  const startModule = async () => {
    return (await axios.post(modulePath('/start'), {}, mutationHeaders)).data;
  };

  const heartbeatModule = async (progress) => {
    await axios.post(modulePath('/heartbeat'), { positionSeconds: progress.positionSeconds }, mutationHeaders);
  };

  const completeModule = async (progress) => {
    setError('');
    await axios.post(modulePath('/complete'), { positionSeconds: progress.positionSeconds }, mutationHeaders);
    const refreshed = await loadModules();
    const done = refreshed.length > 0 && refreshed.every(module => module.completed);
    if (done) await onComplete();
    return true;
  };

  const firstIncompleteIndex = useMemo(
    () => modules.findIndex(module => !module.completed),
    [modules]
  );

  if (loading) return (
    <div className="page-container--wide"><div className="card" style={{ textAlign: 'center', padding: '60px 40px' }}><div className="spinner" style={{ margin: '0 auto 20px' }} /><p style={{ color: '#546E7A' }}>Loading your training program…</p></div></div>
  );

  if (error && modules.length === 0) return (
    <div className="page-container"><div className="card" style={{ textAlign: 'center', padding: '60px 40px' }}><div style={{ fontSize: 40, marginBottom: 16 }}>⚠️</div><h2 style={{ color: '#1B3A5C', marginBottom: 12 }}>Connection Error</h2><p style={{ color: '#546E7A', marginBottom: 24 }}>{error}</p><button className="btn btn-primary" onClick={() => window.location.reload()}>Retry</button></div></div>
  );

  return (
    <div className="page-container--wide">
      <div style={{ marginBottom: 20 }}>
        <div className="step-badge"><span className="step-badge-dot" />Step 2 of 5 — Video Training</div>
        <h1 className="page-title">HIPAA Compliance Training Modules</h1>
        <p className="page-subtitle">
          Welcome, <strong>{userData.fullName}</strong>. Progress is saved securely as you watch; modules must be completed in order.
          <span style={{ display: 'block', marginTop: 8 }}>Complete each lesson to unlock the next. You can rewind or return later.</span>
        </p>
      </div>

      <div className="training-layout">
        <aside className="progress-sidebar">
          <div className="progress-sidebar-title">Training Progress</div>
          <ul className="video-list">
            {modules.map((module, index) => {
              const active = index === currentIndex;
              const available = module.completed || module.available || index === firstIncompleteIndex;
              const status = module.completed ? 'done' : active ? 'active' : 'pending';
              return (
                <li key={module.id}><button type="button" className={`video-list-item video-list-item--${status}`} onClick={() => available && setCurrentIndex(index)} disabled={!available} aria-current={active?'step':undefined} title={available ? module.title : 'Complete previous modules first'}>
                  <div className={`video-list-icon video-list-icon--${status}`}>{module.completed ? '✓' : active ? '▶' : index + 1}</div>
                  <div className="video-list-meta"><div className="video-list-title">{module.title}</div><div className="video-list-label">{index === modules.length - 1 ? `Role: ${userData.role}` : 'All Roles'}{available ? '' : ' 🔒'}</div></div>
                </button></li>
              );
            })}
          </ul>
          <div className="overall-progress"><div className="overall-progress-label"><span>Overall Progress</span><span>{completedCount} / {modules.length}</span></div><div className="overall-progress-bar"><div className="overall-progress-fill" style={{ width: `${overallPercent}%` }} /></div></div>
          <div style={{ marginTop: 20, padding: '14px 16px', background: '#EEF3F8', borderRadius: 7, fontSize: 12, color: '#546E7A' }}><div style={{ fontWeight: 700, color: '#1B3A5C', marginBottom: 4 }}>{userData.fullName}</div><div>{userData.role}</div><div>{userData.officeLocation || userData.office || ''}</div><div style={{ marginTop: 4, wordBreak: 'break-all' }}>{userData.email}</div></div>
        </aside>

        <div className="video-panel">
          <div className="video-panel-header"><div className="video-counter">Module {currentIndex + 1} of {modules.length}</div><h2 className="video-title">{currentModule?.title}</h2><p className="video-desc">{currentModule?.description}</p></div>
          {currentModule?.streamUrl && (
            <VideoPlayer
              key={currentModule.id}
              src={currentModule.streamUrl}
              initialPosition={currentModule.allowedPositionSeconds}
              onStart={startModule}
              onHeartbeat={heartbeatModule}
              onComplete={completeModule}
              isCompleted={currentModule.completed}
            />
          )}
          {error && <div style={{ margin: '14px 24px 0', color: '#C53030', fontSize: 13 }}>{error}</div>}
          <div className="video-panel-footer">
            <div className={`completion-note${currentModule?.completed ? ' completion-note--done' : ''}`}>{currentModule?.completed ? '✓ Lesson complete' : 'Watch through the end to unlock the next lesson'}</div>
            <button className="btn btn-secondary" disabled={!currentModule?.completed} onClick={async () => {
              if (allCompleted) { await onComplete(); return navigate('/quiz'); }
              const next = modules.findIndex((module, index) => index > currentIndex && !module.completed);
              if (next >= 0) setCurrentIndex(next);
            }}>{allCompleted ? 'Return to quiz →' : 'Next lesson →'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
